import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { salesOrdersApi as SO, SO_STATUS_STYLE, PRIORITY_STYLE, inr } from "@/lib/salesOrders";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2 } from "lucide-react";

export default function SalesOrders() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ statuses: [], priorities: [] });
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([SO.stats(), SO.list({ search: search || undefined, status, priority })]);
      setStats(s); setRows(list);
    } finally { setLoading(false); }
  };
  useEffect(() => { SO.meta().then(setMeta); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, priority]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  return (
    <div className="space-y-6" data-testid="sales-orders-page">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Sales Orders</h2>
        <p className="mt-1 text-sm text-slate-500">The manufacturing contract. Created from Accepted quotations — Confirmed orders drive Production.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="p-4" data-testid="so-stat-total"><p className="text-xs uppercase tracking-wider text-slate-500">Total</p><p className="mt-1 font-heading text-2xl font-bold text-slate-900">{stats.total ?? 0}</p></Card>
        <Card className="p-4" data-testid="so-stat-open"><p className="text-xs uppercase tracking-wider text-slate-500">Open (active)</p><p className="mt-1 font-heading text-2xl font-bold text-indigo-600">{stats.open ?? 0}</p></Card>
        <Card className="p-4" data-testid="so-stat-value"><p className="text-xs uppercase tracking-wider text-slate-500">Order value</p><p className="mt-1 font-heading text-xl font-bold text-slate-900">{inr(stats.order_value)}</p></Card>
        <Card className="p-4" data-testid="so-stat-cancelled"><p className="text-xs uppercase tracking-wider text-slate-500">Cancelled</p><p className="mt-1 font-heading text-2xl font-bold text-rose-600">{stats.Cancelled ?? 0}</p></Card>
      </div>

      <Card className="p-4">
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search SO no / customer / quote" className="pl-9" data-testid="so-search-input" />
          </div>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger className="w-40" data-testid="so-priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All priorities</SelectItem>{meta.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-52" data-testid="so-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase text-slate-500">SO No</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Customer</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Priority</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Delivery</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Value</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/sales-orders/${r.id}`)} data-testid={`so-row-${r.so_no}`}>
                    <TableCell className="font-medium text-slate-800">{r.so_no}<span className="block text-xs text-slate-400">from {r.quote_no}</span></TableCell>
                    <TableCell className="text-slate-700">{r.customer?.company_name}</TableCell>
                    <TableCell><Badge className={`hover:bg-inherit ${PRIORITY_STYLE[r.priority]}`}>{r.priority}</Badge></TableCell>
                    <TableCell className="text-slate-600">{r.delivery_date || "—"}</TableCell>
                    <TableCell className="tabular-nums font-medium text-slate-800">{inr(r.totals?.grand_total)}</TableCell>
                    <TableCell><Badge className={`hover:bg-inherit ${SO_STATUS_STYLE[r.status]}`}>{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-slate-400" data-testid="so-empty">No sales orders yet. Convert an Accepted quotation to create one.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
